import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiCheckCircle, FiAlertCircle, FiClock, 
  FiShield, FiCreditCard, FiLock, FiStar,
  FiLoader, FiTarget, FiInfo, FiX, FiTrendingUp,
  FiCalendar, FiZap, FiDollarSign, FiSmartphone, FiMonitor
} from 'react-icons/fi';
import { useAuth } from '../../../context/AuthContext';
import Loading from '../../../components/common/Loading';
import { 
  initializeWalletBoost, 
  processWalletBoostPayment,
  getWalletBalance,
  initializeBoost,
  verifyBoostPayment
} from '../../../services/api';

const BoostPaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listingId } = useParams();
  const { user } = useAuth();
  
  // Get package data from navigation state
  const { package: selectedPackage, pendingOrderId } = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [walletBalance, setWalletBalance] = useState(0);
  const [orderId, setOrderId] = useState(null);
  const [paymentInitialized, setPaymentInitialized] = useState(false);
  const [polling, setPolling] = useState(false);
  
  // Initialize page
  useEffect(() => {
    if (!selectedPackage || !user) {
      navigate(-1);
      return;
    }
    
    initializePage();
  }, [selectedPackage, user, navigate]);
  
  const initializePage = async () => {
    try {
      // Fetch wallet balance
      const balance = await fetchWalletBalance();
      
      // Auto-select payment method based on balance
      if (balance < selectedPackage.price) {
        setPaymentMethod('card');
      }
      
      setLoading(false);
    } catch (error) {
      // console.error('Page initialization error:', error);
      setError('Failed to load payment details. Please try again.');
      setLoading(false);
    }
  };
  
  const fetchWalletBalance = async () => {
    try {
      const response = await getWalletBalance(user.user_id);
      
      let balance = 0;
      if (response.success !== false) {
        if (response.balance) {
          balance = parseFloat(response.balance);
        } else if (response.data?.balance) {
          balance = parseFloat(response.data.balance);
        } else if (response.wallet?.balance) {
          balance = parseFloat(response.wallet.balance);
        }
      }
      
      setWalletBalance(balance);
      return balance;
    } catch (error) {
      // console.error('Error fetching wallet balance:', error);
      setWalletBalance(0);
      return 0;
    }
  };
  
  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };
  
  const calculateExpiryDate = () => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + selectedPackage.duration_days);
    return expiry.toLocaleDateString('en-NG', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const handleInitializePayment = async () => {
    if (processing) return;
    
    setError(null);
    setProcessing(true);
    
    try {
      const boostData = {
        listing_id: parseInt(listingId),
        package_id: selectedPackage.package_id,
        user_id: user.user_id,
        user_email: user.email
      };
      
      const response = await initializeWalletBoost(boostData);
      
      if (response.success) {
        setOrderId(response.order_id);
        setPaymentInitialized(true);
        
        // Update wallet balance
        await fetchWalletBalance();
      } else {
        throw new Error(response.error || 'Failed to initialize payment');
      }
      
      setProcessing(false);
      
    } catch (error) {
      // console.error('Payment initialization error:', error);
      setError(error.message || 'Failed to initialize payment. Please try again.');
      setProcessing(false);
    }
  };
  
  const handleWalletPayment = async () => {
    if (processing) return;
    
    setError(null);
    setProcessing(true);
    
    try {
      if (!orderId) {
        throw new Error('Payment not initialized');
      }
      
      const response = await processWalletBoostPayment(orderId, {
        user_id: user.user_id
      });
      
      if (response.success) {
        setSuccess(true);
        
        // Redirect after success
        setTimeout(() => {
          navigate(`/listings/${listingId}`, {
            state: { 
              boostSuccess: true,
              packageName: selectedPackage.name,
              expiresAt: response.expires_at
            }
          });
        }, 2500);
        
      } else {
        throw new Error(response.error || 'Payment failed');
      }
      
    } catch (error) {
      // console.error('Wallet payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };
  
  const handleCardPayment = async () => {
    if (processing) return;
    
    setError(null);
    setProcessing(true);
    
    try {
      const boostData = {
        listing_id: parseInt(listingId),
        package_id: selectedPackage.package_id,
        user_id: user.user_id,
        user_email: user.email
      };
      
      const response = await initializeBoost(boostData);
      
      if (response.success) {
        // Open Paystack in new tab
        const paymentWindow = window.open(response.payment_url, '_blank', 'noopener,noreferrer');
        
        if (!paymentWindow) {
          // If popup blocked, show alert
          alert('Please allow popups to complete payment. Then click OK to continue.');
        }
        
        // Start polling for payment status
        startPaymentPolling(response.order_id, response.reference);
        
        setProcessing(false);
        
      } else {
        throw new Error(response.error || 'Failed to initialize card payment');
      }
      
    } catch (error) {
      // console.error('Card payment error:', error);
      setError(error.message || 'Payment initialization failed. Please try again.');
      setProcessing(false);
    }
  };
  
  const startPaymentPolling = (orderId, reference) => {
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 60; // 60 seconds
    
    const poll = async () => {
      attempts++;
      
      try {
        const response = await verifyBoostPayment(orderId);
        
        if (response.success) {
          if (response.payment_status === 'paid') {
            // Payment successful
            setSuccess(true);
            setPolling(false);
            
            setTimeout(() => {
              navigate(`/store/listings/listing-details/${listingId}`, {
                state: { 
                  boostSuccess: true,
                  packageName: selectedPackage.name
                }
              });
            }, 2000);
            
            return;
          } else if (response.payment_status === 'failed') {
            setError('Payment failed. Please try again.');
            setPolling(false);
            return;
          }
        }
        
        // Continue polling
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('Payment verification timed out. Please check your email for confirmation.');
          setPolling(false);
        }
      } catch (error) {
        // console.error('Polling error:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('Could not verify payment status.');
          setPolling(false);
        }
      }
    };
    
    // Start polling after 2 seconds
    setTimeout(poll, 2000);
  };
  
  const handleAddFunds = () => {
    navigate('/wallet/add-funds', {
      state: { returnUrl: window.location.pathname }
    });
  };
  
  const handleBack = () => {
    navigate(-1);
  };
  
  // Responsive breakpoints
  const isMobile = window.innerWidth < 768;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Loading text="Loading payment details..." />
      </div>
    );
  }
  
  if (!selectedPackage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full">
          <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">No Package Selected</h2>
          <p className="text-gray-600 mb-6">Please select a boost package first.</p>
          <button
            onClick={handleBack}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors w-full md:w-auto"
          >
            Back to Packages
          </button>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Payment Successful! 🎉</h1>
            <p className="text-gray-600">Your listing is now boosted with {selectedPackage.name}</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-5 md:p-6 mb-6 border border-green-200">
            <div className="space-y-3 md:space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Package</span>
                <span className="font-semibold">{selectedPackage.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Duration</span>
                <span className="font-semibold">{selectedPackage.duration_days} days</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Amount</span>
                <span className="text-xl font-bold text-green-600">
                  {formatPrice(selectedPackage.price)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Payment Method</span>
                <span className="font-semibold capitalize">{paymentMethod}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/listings/${listingId}`)}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-md"
            >
              View Boosted Listing
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full border border-gray-300 text-gray-700 px-6 py-3.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const insufficientBalance = walletBalance < selectedPackage.price;
  const canProceedWallet = paymentMethod === 'wallet' && 
                         (!paymentInitialized || (paymentInitialized && !insufficientBalance));
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
              aria-label="Go back"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg md:text-xl font-bold text-gray-900">Complete Boost Purchase</h1>
              <p className="text-xs md:text-sm text-gray-600 truncate">Finalize your boost package</p>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Left Column: Package & Payment */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* Package Summary Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-5 border border-orange-200">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">Selected Package</h2>
                  <p className="text-xs md:text-sm text-gray-600">Boost details</p>
                </div>
                {selectedPackage.is_popular && (
                  <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 px-2 md:px-3 py-1 rounded-full text-xs font-medium">
                    <FiStar className="w-3 h-3" />
                    Popular
                  </span>
                )}
              </div>
              
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-3 md:p-4 mb-4 border border-orange-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3">
                  <div>
                    <h3 className="font-bold text-orange-900 text-base md:text-lg">{selectedPackage.name}</h3>
                    <p className="text-xs md:text-sm text-orange-700">Active for {selectedPackage.duration_days} days</p>
                  </div>
                  <span className="text-2xl md:text-3xl font-bold text-orange-600">
                    {formatPrice(selectedPackage.price)}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs md:text-sm">
                  <div className="flex items-center gap-1.5">
                    <FiCalendar className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-gray-700">Expires {calculateExpiryDate()}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <FiZap className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-gray-700">Instant activation</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">{selectedPackage.description}</p>
            </div>
            
            {/* Payment Methods Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-5 border">
              <h2 className="font-semibold text-gray-900 text-base md:text-lg mb-3 md:mb-4">Payment Method</h2>
              
              <div className="space-y-3">
                {/* Wallet Option */}
                <div className={`border rounded-xl p-3 md:p-4 transition-all ${paymentMethod === 'wallet' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${paymentMethod === 'wallet' ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <FiCreditCard className={`w-4 h-4 md:w-5 md:h-5 ${paymentMethod === 'wallet' ? 'text-green-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm md:text-base">Pay with Wallet</h3>
                        <p className="text-gray-600 text-xs md:text-sm">Use your wallet balance</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPaymentMethod('wallet')}
                      className={`w-5 h-5 md:w-6 md:h-6 rounded-full border flex items-center justify-center ${
                        paymentMethod === 'wallet' 
                          ? 'bg-green-500 border-green-500' 
                          : 'bg-white border-gray-300'
                      }`}
                      aria-label="Select wallet payment"
                    >
                      {paymentMethod === 'wallet' && <FiCheckCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />}
                    </button>
                  </div>
                  
                  {paymentMethod === 'wallet' && (
                    <div className="mt-3 p-3 bg-white/80 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-xs font-medium text-green-800">Wallet Balance</p>
                          <p className="text-xl md:text-2xl font-bold text-green-900">
                            {formatPrice(walletBalance)}
                          </p>
                        </div>
                        <button
                          onClick={fetchWalletBalance}
                          className="text-green-600 hover:text-green-700 p-1"
                          type="button"
                          aria-label="Refresh balance"
                        >
                          <FiLoader className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {insufficientBalance && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-1.5">
                            <FiAlertCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-red-800">Insufficient Balance</p>
                              <p className="text-xs text-red-700">
                                Need {formatPrice(selectedPackage.price - walletBalance)} more
                              </p>
                              <button
                                onClick={handleAddFunds}
                                className="text-xs text-red-600 underline mt-0.5"
                                type="button"
                              >
                                Add Funds →
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Card Option */}
                <div className={`border rounded-xl p-3 md:p-4 transition-all ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${paymentMethod === 'card' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <FiCreditCard className={`w-4 h-4 md:w-5 md:h-5 ${paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm md:text-base">Pay with Card</h3>
                        <p className="text-gray-600 text-xs md:text-sm">Debit/Credit card via Paystack</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPaymentMethod('card')}
                      className={`w-5 h-5 md:w-6 md:h-6 rounded-full border flex items-center justify-center ${
                        paymentMethod === 'card' 
                          ? 'bg-blue-500 border-blue-500' 
                          : 'bg-white border-gray-300'
                      }`}
                      aria-label="Select card payment"
                    >
                      {paymentMethod === 'card' && <FiCheckCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Security Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 md:p-4">
              <div className="flex items-start gap-2 md:gap-3">
                <FiLock className="w-4 h-4 md:w-5 md:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-800 text-sm md:text-base">Secure Payment</h3>
                  <p className="text-green-700 text-xs md:text-sm mt-0.5">
                    All payments are processed securely. Your listing will be boosted immediately after successful payment.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column: Summary & Actions */}
          <div className="space-y-4 md:space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-5 border border-gray-200">
              <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-3 md:mb-4">Order Summary</h3>
              
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Package:</span>
                  <span className="font-medium text-sm">{selectedPackage.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Duration:</span>
                  <span className="font-medium text-sm">{selectedPackage.duration_days} days</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Payment Method:</span>
                  <span className="font-medium text-sm capitalize">{paymentMethod}</span>
                </div>
                <div className="border-t pt-2 md:pt-3 mt-2 md:mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-orange-600">
                      {formatPrice(selectedPackage.price)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">No additional fees</p>
                </div>
              </div>
            </div>
            
            {/* Benefits Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 md:p-5">
              <h3 className="font-semibold text-blue-900 text-base md:text-lg mb-2 md:mb-3">Boost Benefits</h3>
              <ul className="space-y-1.5 md:space-y-2">
                <li className="flex items-center gap-1.5">
                  <FiTrendingUp className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-800 text-xs md:text-sm">5-10x more views</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <FiTarget className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-800 text-xs md:text-sm">Top search positions</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <FiZap className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-800 text-xs md:text-sm">Instant activation</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <FiShield className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-800 text-xs md:text-sm">Secure payment</span>
                </li>
              </ul>
            </div><br /><br /><br /><br /><br /><br />
          </div>
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 md:p-4">
            <div className="flex items-start gap-2 md:gap-3">
              <FiAlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 text-sm md:text-base">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)} 
                className="text-red-600 hover:text-red-800 p-1"
                aria-label="Dismiss error"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* Fixed Payment Button (Mobile) / Sticky Button (Desktop) */}
      <div className={`${isMobile ? 'fixed bottom-0 left-0 right-0' : 'sticky bottom-4 mt-6'} bg-white border-t shadow-lg z-40`}>
        <div className="max-w-6xl mx-auto px-4 py-3 md:py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-4">
            <div className="flex-1">
              <p className="text-xs text-gray-600">Total to pay</p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl md:text-2xl font-bold text-gray-900">
                  {formatPrice(selectedPackage.price)}
                </p>
                {paymentMethod === 'wallet' && insufficientBalance && (
                  <span className="text-xs text-red-600 font-medium">
                    (Insufficient balance)
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 md:gap-3 w-full md:w-auto">
              {paymentMethod === 'wallet' && !paymentInitialized && !insufficientBalance && (
                <button
                  onClick={handleInitializePayment}
                  disabled={processing}
                  className="flex-1 md:flex-none px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {processing ? 'Initializing...' : 'Initialize'}
                </button>
              )}
              
              <button
                onClick={paymentMethod === 'wallet' ? 
                  (paymentInitialized ? handleWalletPayment : handleInitializePayment) : 
                  handleCardPayment}
                disabled={processing || polling || (paymentMethod === 'wallet' && insufficientBalance)}
                className={`flex-1 md:flex-none px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md ${
                  processing || polling
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : paymentMethod === 'wallet' && insufficientBalance
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                }`}
              >
                {processing || polling ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Processing...
                  </span>
                ) : paymentMethod === 'wallet' ? (
                  paymentInitialized ? 'Pay with Wallet' : 'Initialize Payment'
                ) : (
                  'Pay with Card'
                )}
              </button>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-2">
            By proceeding, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
};

export default BoostPaymentPage;